import React, { useState, useEffect } from 'react';

export default function EditUserModal({ 
  onClose, 
  onUpdate, 
  editingUser, 
  availableRoles = [],
  schools = [],
  loading = false 
}) {
  const [formData, setFormData] = useState({
    name: editingUser?.name || '',
    role: editingUser?.role || '',
    accessType: ((editingUser?.accessible_school_ids || editingUser?.accessibleSchoolIds || []).length > 0) ? 'school' : 'organization',
    accessibleSchoolIds: editingUser?.accessible_school_ids || editingUser?.accessibleSchoolIds || []
  });

  const [errors, setErrors] = useState({});

  // Update form data when editingUser changes
  useEffect(() => {
    if (editingUser) {
      // Clean up accessibleSchoolIds by removing any schools that no longer exist
      // Backend uses snake_case (accessible_school_ids), frontend uses camelCase
      const originalSchoolIds = editingUser.accessible_school_ids || editingUser.accessibleSchoolIds || [];
      const validSchoolIds = originalSchoolIds.filter(schoolId => 
        schools.find(school => school.id === schoolId)
      );
      
      // Determine access type based on valid school IDs
      const accessType = validSchoolIds.length > 0 ? 'school' : 'organization';
      
      setFormData({
        name: editingUser.name || '',
        role: editingUser.role || '',
        accessType: accessType,
        accessibleSchoolIds: validSchoolIds
      });

      // Log if we had to clean up any invalid school IDs
      if (originalSchoolIds.length !== validSchoolIds.length) {
        console.log(`Cleaned up ${originalSchoolIds.length - validSchoolIds.length} invalid school IDs for user ${editingUser.name}`);
      }
    }
  }, [editingUser, schools]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Validate school selection for school access type
    if (formData.accessType === 'school' && formData.accessibleSchoolIds.length === 0) {
      newErrors.schools = 'Please select at least one school for specific school access';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const updateData = {
      name: formData.name.trim(),
      role: formData.role,
      // Backend expects snake_case field name
      accessible_school_ids: formData.accessType === 'organization' ? [] : formData.accessibleSchoolIds
    };

    onUpdate(updateData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing or making changes
    if (errors[field] || (field === 'role' && errors.schools)) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
        ...(field === 'role' ? { schools: '' } : {})
      }));
    }
  };

  // Handle school checkbox changes
  const handleSchoolToggle = (schoolId) => {
    setFormData(prev => ({
      ...prev,
      accessibleSchoolIds: prev.accessibleSchoolIds.includes(schoolId)
        ? prev.accessibleSchoolIds.filter(id => id !== schoolId)
        : [...prev.accessibleSchoolIds, schoolId]
    }));

    // Clear school validation errors
    if (errors.schools) {
      setErrors(prev => ({ ...prev, schools: '' }));
    }
  };

  // Handle access type change (similar to handleInviteTypeChange in AdminPanel)
  const handleAccessTypeChange = (newAccessType) => {
    setFormData(prev => ({
      ...prev,
      accessType: newAccessType,
      role: '', // Reset role when access type changes
      accessibleSchoolIds: newAccessType === 'organization' ? [] : prev.accessibleSchoolIds
    }));

    // Clear validation errors when access type changes
    setErrors(prev => ({ ...prev, role: '', schools: '' }));
  };

  // Get available roles based on access type (similar to getAvailableRoles in AdminPanel)
  const getFilteredRoles = () => {
    if (formData.accessType === 'organization') {
      // For organization-wide access, filter to organization-scoped roles
      return availableRoles.filter(role => 
        ['tenant_administrator', 'organization_admin', 'organization_teacher', 'organization_staff'].includes(role.value)
      );
    } else {
      // For school-specific access, filter to school-scoped roles
      return availableRoles.filter(role => 
        ['school_admin', 'teacher', 'substitute', 'support_staff'].includes(role.value)
      );
    }
  };

  // Handle "All Schools" checkbox
  const handleAllSchoolsToggle = () => {
    const allSchoolIds = schools.map(school => school.id);
    const hasAllSchools = allSchoolIds.every(id => formData.accessibleSchoolIds.includes(id));
    
    setFormData(prev => ({
      ...prev,
      accessibleSchoolIds: hasAllSchools ? [] : allSchoolIds
    }));

    // Clear school validation errors
    if (errors.schools) {
      setErrors(prev => ({ ...prev, schools: '' }));
    }
  };

  // Check if user has access to all schools
  const hasAllSchools = () => {
    if (formData.accessibleSchoolIds.length === 0) return false;
    const allSchoolIds = schools.map(school => school.id);
    return allSchoolIds.every(id => formData.accessibleSchoolIds.includes(id));
  };

  // Check if role requires school-specific access
  const requiresSchoolSelection = () => {
    const schoolScopedRoles = ['teacher', 'substitute', 'support_staff', 'school_admin'];
    return schoolScopedRoles.includes(formData.role);
  };

  if (!editingUser) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h2 className="text-2xl font-bold text-slate-800">Edit User</h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {/* Name Field */}
          <div className="mb-6">
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                errors.name
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-slate-200 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="John Adams"
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Email Field (Read-only) */}
          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={editingUser.email}
              readOnly
              className="w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 text-slate-500 cursor-not-allowed"
              placeholder="johnadams@gmail.com"
            />
            <p className="mt-1 text-xs text-slate-500">Email cannot be changed</p>
          </div>

          {/* Role Field */}
          <div className="mb-6">
            <label htmlFor="role" className="block text-sm font-medium text-slate-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-colors ${
                errors.role
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-slate-200 focus:ring-blue-500 focus:border-blue-500'
              }`}
              disabled={loading}
            >
              <option value="">Select a role</option>
              {getFilteredRoles().map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role}</p>
            )}
          </div>

          {/* Access Type Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-3">
              Access Type
            </label>
            <div className="space-y-3">
              {/* Organization-wide Access */}
              <div className="flex items-start">
                <input
                  type="radio"
                  id="organization_access"
                  name="accessType"
                  value="organization"
                  checked={formData.accessType === 'organization'}
                  onChange={(e) => handleAccessTypeChange(e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 mt-0.5"
                  disabled={loading}
                />
                <div className="ml-3">
                  <label htmlFor="organization_access" className="text-sm font-medium text-slate-700">
                    Organization-wide Access
                  </label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Access to all schools in the organization
                  </p>
                </div>
              </div>

              {/* Specific School Access */}
              <div className="flex items-start">
                <input
                  type="radio"
                  id="school_access"
                  name="accessType"
                  value="school"
                  checked={formData.accessType === 'school'}
                  onChange={(e) => handleAccessTypeChange(e.target.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 mt-0.5"
                  disabled={loading}
                />
                <div className="ml-3">
                  <label htmlFor="school_access" className="text-sm font-medium text-slate-700">
                    Specific School Access
                  </label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Access limited to selected schools only
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* School Selection - Only show when Specific School Access is selected */}
          {formData.accessType === 'school' && schools.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Select Schools
              </label>
              <div className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                <div className="space-y-3">
                  {/* All Schools checkbox */}
                  <div className="flex items-center pb-2 border-b border-slate-200">
                    <input
                      type="checkbox"
                      id="all_schools"
                      checked={hasAllSchools()}
                      onChange={handleAllSchoolsToggle}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                      disabled={loading}
                    />
                    <label htmlFor="all_schools" className="ml-2 text-sm font-medium text-slate-700">
                      Select All Schools
                    </label>
                  </div>

                  {/* Individual School Selection */}
                  <div className="space-y-2">
                    {schools.map((school) => (
                      <div key={school.id} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`school_${school.id}`}
                          checked={formData.accessibleSchoolIds.includes(school.id)}
                          onChange={() => handleSchoolToggle(school.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                          disabled={loading}
                        />
                        <label htmlFor={`school_${school.id}`} className="ml-2 text-sm text-slate-700">
                          {school.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Validation message for school-scoped roles */}
              {requiresSchoolSelection() && formData.accessibleSchoolIds.length === 0 && (
                <p className="mt-2 text-sm text-amber-600">
                  This role typically requires specific school access. Please select at least one school.
                </p>
              )}

              {/* Validation error for school selection */}
              {errors.schools && (
                <p className="mt-2 text-sm text-red-600">{errors.schools}</p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors duration-200"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </>
              ) : (
                'Update User'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}